const isOn = (flag) => Number(flag) === 1;

const asList = (value) => (Array.isArray(value) ? value : []);

const withStoredValue = (list, stored) => (stored && !list.includes(stored) ? [...list, stored] : list);

const storedOnly = (stored) => (stored ? [stored] : []);

export const normalizeRenderingOptions = (data) => ({
  elementTypes: asList(data?.elementTypes).map((element) => ({
    name: element?.name,
    usesGrass: isOn(element?.usesGrass),
    usesCharacteristic: isOn(element?.usesCharacteristic),
    characteristics: asList(element?.characteristics),
  })),
  grassTypes: asList(data?.grassTypes),
  seasons: asList(data?.seasons),
});

export const findElementType = (options, name) =>
  options?.elementTypes?.find((element) => element.name === name);

const activeElementNames = (rows, exceptRow) =>
  (rows || [])
    .filter((row) => row !== exceptRow && row?.active === 1 && row?.elementtype)
    .map((row) => row.elementtype);

export const availableElementTypes = (options, rows) => {
  const used = activeElementNames(rows);
  return options.elementTypes.map((element) => element.name).filter((name) => !used.includes(name));
};

export const elementOptionsForRow = (options, row, rows) =>
  withStoredValue(
    options.elementTypes.map((element) => element.name).filter((name) => !activeElementNames(rows, row).includes(name)),
    row?.elementtype,
  );

export const grassOptionsForRow = (options, row) => {
  const element = findElementType(options, row?.elementtype);
  if (!element) return storedOnly(row?.grasstype);
  if (!element.usesGrass) return [];
  return withStoredValue(options.grassTypes, row?.grasstype);
};

export const characteristicOptionsForRow = (options, row) => {
  const element = findElementType(options, row?.elementtype);
  if (!element) return storedOnly(row?.characteristic);
  if (!element.usesCharacteristic) return [];
  return withStoredValue(element.characteristics, row?.characteristic);
};

export const seasonOptionsForRow = (options, row) => withStoredValue(options.seasons, row?.seasonality);

export const toRenderingPayloadRow = (options, row) => {
  const element = findElementType(options, row?.elementtype);
  const grassAllowed = element ? element.usesGrass : true;
  const characteristicAllowed = element ? element.usesCharacteristic : true;
  const payload = {
    active: row?.active ?? 1,
    elementtype: row?.elementtype,
    seasonality: row?.seasonality,
  };
  if (grassAllowed && row?.grasstype) payload.grasstype = row.grasstype;
  if (characteristicAllowed && row?.characteristic) payload.characteristic = row.characteristic;
  return payload;
};

const findDuplicateElementError = (rows) => {
  const firstIndexByName = {};
  for (let index = 0; index < rows.length; index += 1) {
    const name = rows[index]?.elementtype;
    if (!name) continue;
    if (firstIndexByName[name] !== undefined) {
      return `Rendering rows ${firstIndexByName[name] + 1} and ${index + 1} both use ${name}. A course can have only one element per type.`;
    }
    firstIndexByName[name] = index;
  }
  return null;
};

export const findRenderingRowError = (options, rows) => {
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    const position = `Rendering row ${index + 1}`;
    if (!row?.elementtype) return `${position}: Element is required.`;
    const labelled = `${position} (${row.elementtype})`;
    if (!row?.seasonality) return `${labelled}: Season is required.`;
    const element = findElementType(options, row.elementtype);
    if (!element) continue;
    if (element.usesGrass && !row?.grasstype) return `${labelled}: Grass Type is required.`;
    if (element.usesCharacteristic && !row?.characteristic) return `${labelled}: Characteristic is required.`;
  }
  return findDuplicateElementError(rows);
};

const resolveDependentValue = (allowed, enabled, current) => {
  if (!enabled) return "";
  if (current && allowed.includes(current)) return current;
  return allowed[0] || "";
};

export const applyElementChange = (options, row, nextElementName) => {
  const element = findElementType(options, nextElementName);
  return {
    ...row,
    elementtype: nextElementName,
    grasstype: resolveDependentValue(options.grassTypes, Boolean(element?.usesGrass), row?.grasstype),
    characteristic: resolveDependentValue(
      element?.characteristics || [],
      Boolean(element?.usesCharacteristic),
      row?.characteristic,
    ),
  };
};

export const buildNewRenderingRow = (options, rows) => {
  const nextElement = availableElementTypes(options, rows)[0];
  if (!nextElement) return null;
  const base = {
    elementtype: "",
    grasstype: "",
    seasonality: options.seasons[0] || "",
    characteristic: "",
    active: 1,
  };
  return applyElementChange(options, base, nextElement);
};
